<template>
  <aside
    class="bg-teal pc:w-64 pc:static pc:translate-x-0 pc:h-screen mobile:fixed mobile:top-0 mobile:left-0 mobile:z-100 mobile:h-full mobile:w-64 mobile:transition-transform mobile:duration-[250ms] mobile:ease-in-out flex h-full shrink-0 flex-col text-sm text-white shadow-xl"
    :class="[drawer ? 'mobile:translate-x-0' : 'mobile:-translate-x-full']"
  >
    <div class="shrink-0 p-4">
      <RouterLink to="/" class="block">
        <img src="../../assets/logo-large.png" alt="Chapitô — accueil" />
      </RouterLink>
    </div>
    <UiDivider class="mx-5 shrink-0" />
    <div class="shrink-0">
      <div class="mx-auto mt-5 w-20">
        <UiImg
          v-if="!!icon"
          class="rounded-[20%] shadow-[0_10px_30px_-12px_rgba(0,0,0,0.42),0_4px_25px_0px_rgba(0,0,0,0.12),0_8px_10px_-5px_rgba(0,0,0,0.2)]"
          :src="`/api/thumb/${icon}_square`"
        />
      </div>
      <div class="my-5 ml-3 flex justify-center text-white">
        {{ username }}
        <UiMenu :closeOnContentClick="true">
          <UiIcon dark name="ellipsis-vertical" />
          <template #menu="{ close }">
            <UiList>
              <UiListItem
                @click="
                  () => {
                    logout()
                    close()
                  }
                "
              >
                <div class="truncate text-sm font-medium text-gray-900">
                  {{ $t('APP_LAYOUT.LOGOUT') }}
                </div>
              </UiListItem>
            </UiList>
          </template>
        </UiMenu>
      </div>
    </div>
    <UiDivider class="mx-5 shrink-0" />
    <template v-if="isDirty">
      <div class="grid shrink-0 p-4">
        <UiButton
          size="small"
          variant="success"
          @click="publish()"
          class="whitespace-break-spaces!"
        >
          <UiIcon left right name="cloud-upload" />
          {{ $t('APP_LAYOUT.PUBLISH') }}
        </UiButton>
      </div>
      <UiDivider class="mx-5 shrink-0" />
    </template>
    <ul class="menu-list min-h-0 flex-1 overflow-y-auto">
      <template
        v-for="field in getFilteredFields(menuFields)"
        :key="field.text"
      >
        <li
          v-if="
            'subFields' in field &&
            !!field.subFields &&
            getFilteredFields(field.subFields).length > 1
          "
          class="my-2"
          :id="field.id"
        >
          <button
            type="button"
            class="flex h-12 w-full cursor-pointer items-center justify-between gap-4 px-4 text-left hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none"
            :class="{ 'font-semibold': isGroupActive(field) }"
            :id="field.id ? field.id + '-activator' : undefined"
            :aria-expanded="isSubmenuOpen(field.text)"
            @click="toggleSubmenu(field.text)"
          >
            <span class="flex gap-4">
              <UiIcon dark :name="field.icon" />
              {{ field.text }}
            </span>
            <UiIcon
              dark
              name="chevron-down"
              class="transition-transform duration-200"
              :class="{ 'rotate-180': isSubmenuOpen(field.text) }"
            />
          </button>
          <div
            class="grid transition-all duration-200"
            :class="
              isSubmenuOpen(field.text) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            "
          >
            <!-- inert: collapsed links must not be reachable with Tab -->
            <ul class="overflow-hidden" :inert="!isSubmenuOpen(field.text)">
              <li
                v-for="subfield in getFilteredFields(field.subFields)"
                :key="subfield.text"
                :id="subfield.id"
                class="my-2"
              >
                <RouterLink
                  :to="subfield.link"
                  :class="[linkClasses, 'pl-14', activeClasses(subfield.link)]"
                  :aria-current="
                    isLinkActive(subfield.link) ? 'page' : undefined
                  "
                >
                  {{ subfield.text }}
                </RouterLink>
              </li>
            </ul>
          </div>
        </li>
        <!-- A group with a single visible entry is a direct link (Vue 2) -->
        <li
          v-else-if="
            !field.subFields || getFilteredFields(field.subFields).length > 0
          "
          :key="field.text"
          class="my-2"
        >
          <RouterLink
            :to="getLeafLink(field)"
            :class="[linkClasses, activeClasses(getLeafLink(field))]"
            :aria-current="
              isLinkActive(getLeafLink(field)) ? 'page' : undefined
            "
          >
            <UiIcon dark :name="'icon' in field ? field.icon : ''" />
            <span>
              {{
                'subFields' in field && !!field.subFields
                  ? getFilteredFields(field.subFields)[0]?.text
                  : field.text
              }}
            </span>
          </RouterLink>
        </li>
      </template>
    </ul>
    <UpToDate v-if="isAdmin" />
  </aside>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import UiButton from '@/shared/components/ui/UiButton.vue'
import UiDivider from '@/shared/components/ui/UiDivider.vue'
import UiIcon from '@/shared/components/ui/UiIcon.vue'
import UiImg from '@/shared/components/ui/UiImg.vue'
import UiList from '@/shared/components/ui/UiList.vue'
import UiListItem from '@/shared/components/ui/UiListItem.vue'
import UiMenu from '@/shared/components/ui/UiMenu.vue'

import { type FeatureName } from '../../pages/configDev/features'
import { usePublicationsStore } from '../../pages/publications/publications.store'
import { useStoresInformationsStore } from '../../pages/storesInformations/storesInformations.store'
import UpToDate from '../../shared/components/UpToDate.vue'
import { useAppConfigStore } from '../../shared/store/app-config.store'
import { useSessionStore } from '../../shared/store/session.store'

type Menufield = {
  text: string
  icon: string
  id?: string
  link?: string
  showIf: boolean
  subFields?: MenuSubField[]
}
type MenuSubField = {
  text: string
  link: string
  id?: string
  showIf: boolean
}

const props = defineProps<{
  modelValue?: boolean
  value?: boolean
}>()
const emit = defineEmits<(e: 'update:modelValue', val: boolean) => void>()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const sessionStore = useSessionStore()
const username = computed(() => sessionStore.principal?.name ?? '')
const storesInformationsStore = useStoresInformationsStore()
const icon = computed(() => storesInformationsStore.icon)
const isAdmin = computed(
  () => sessionStore.principal?.roles.includes('admin') ?? false,
)
const appConfigStore = useAppConfigStore()
const isDirty = computed(() => appConfigStore.appConfig?.isDirty ?? false)
const featureIsEnable = computed(
  () => (feature: FeatureName) =>
    appConfigStore.appConfig?.features[feature] ?? false,
)

const drawer = computed({
  get() {
    return props.modelValue || props.value
  },
  set(val: boolean) {
    emit('update:modelValue', val)
  },
})

const openSubmenus = ref<string[]>([])

function toggleSubmenu(key: string) {
  const next = openSubmenus.value.includes(key)
    ? openSubmenus.value.filter((k) => k !== key)
    : [...openSubmenus.value, key]
  openSubmenus.value = next
}

function isSubmenuOpen(key: string) {
  return openSubmenus.value.includes(key)
}

const linkClasses =
  'flex h-12 items-center gap-4 px-4 hover:bg-white/10 focus-visible:bg-white/10 focus-visible:outline-none'

/** Same matching as Vuetify's v-list-item `to` (vue-router 3 active class):
 * exact for '/', path prefix otherwise (e.g. /guests/123 → Guests). */
function isLinkActive(link: string): boolean {
  if (link === '' || link === '/') return route.path === link
  return route.path === link || route.path.startsWith(`${link}/`)
}

function activeClasses(link: string): string {
  return isLinkActive(link) ? 'bg-black/25 font-semibold' : ''
}

function getLeafLink(field: Menufield): string {
  return field.subFields
    ? (getFilteredFields(field.subFields)[0]?.link ?? '')
    : (field.link ?? '')
}

function isGroupActive(field: Menufield): boolean {
  return getFilteredFields(field.subFields ?? []).some((subfield) =>
    isLinkActive(subfield.link),
  )
}

const menuFields = computed<Menufield[]>(() => {
  return [
    {
      text: t('APP_LAYOUT.MENU.HOME'),
      icon: 'dashboard',
      link: '/',
      showIf: true,
    },
    {
      text: t('APP_LAYOUT.MENU.PROGRAMMATION'),
      icon: 'calendar_today',
      link: '/programmation',
      showIf: true,
    },
    {
      text: t('APP_LAYOUT.MENU.DATA'),
      icon: 'group',
      showIf: true,
      subFields: [
        {
          text: t('APP_LAYOUT.MENU.PROGRAM'),
          link: '/programs',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.SCENES'),
          link: '/scenes',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.GUEST_TYPE'),
          link: '/guest-types',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.GUESTS'),
          link: '/guests',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.GENRES'),
          link: '/genres',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.ATTENDEES'),
          link: '/attendees',
          showIf:
            featureIsEnable.value('showAttendees') ||
            featureIsEnable.value('loginWallEnabled'),
        },
        {
          text: t('APP_LAYOUT.MENU.NEWS_ARTICLES'),
          link: '/newsArticles',
          showIf: featureIsEnable.value('allowEgaluantesImport'),
        },
        {
          text: t('APP_LAYOUT.MENU.IMPORT'),
          link: '/import',
          showIf:
            featureIsEnable.value('allowHeedsImport') ||
            featureIsEnable.value('allowLePetitGarageImport') ||
            featureIsEnable.value('allowEgaluantesImport'),
        },
      ],
    },
    {
      text: t('APP_LAYOUT.MENU.INFORMATIONS'),
      icon: 'info',
      showIf: true,
      subFields: [
        {
          text: t('APP_LAYOUT.MENU.SPONSORS'),
          link: '/sponsors',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.INFO'),
          link: '/infosPratiques',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.GALLERY'),
          link: '/gallery',
          showIf: featureIsEnable.value('showDrawerEntryForGallery'),
        },
      ],
    },
    {
      text: t('APP_LAYOUT.MENU.SETTINGS'),
      icon: 'apps',
      showIf: true,
      id: 'chapi-settings-menu',
      subFields: [
        {
          text: t('APP_LAYOUT.MENU.GENERAL'),
          link: '/festival',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.STORES'),
          link: '/store',
          showIf: true,
        },
        {
          link: '/splashScreen',
          id: 'chapi-splashScreen-menu',
          text: t('APP_LAYOUT.MENU.SPLASH_SCREEN'),
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.APPLICATION_CFG'),
          link: '/applicationCfg',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.WHITELIST'),
          link: '/attendee-white-list',
          showIf:
            featureIsEnable.value('showAttendees') ||
            featureIsEnable.value('loginWallEnabled'),
        },
        {
          text: t('APP_LAYOUT.MENU.MENU'),
          link: '/menuEntries',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.PRIVATE_SECTIONS'),
          link: '/privateSections',
          showIf: featureIsEnable.value('privateSectionsEnabled'),
        },
      ],
    },
    {
      text: t('APP_LAYOUT.MENU.NOTIFICATIONS'),
      icon: 'notifications',
      showIf: true,
      subFields: [
        {
          text: t('APP_LAYOUT.MENU.TOPICS'),
          link: '/notificationstopics',
          showIf: featureIsEnable.value('notificationTopicsEnabled'),
        },
        {
          text: t('APP_LAYOUT.MENU.LIST_NOTIF'),
          link: '/notifications',
          showIf: true,
        },
      ],
    },
    {
      text: t('APP_LAYOUT.MENU.VIDEOS'),
      icon: 'videocam',
      showIf: featureIsEnable.value('videoGeneration'),
      link: '/video',
    },
    {
      text: t('APP_LAYOUT.MENU.MAP'),
      icon: 'map',
      showIf: featureIsEnable.value('allowCustomMap'),
      link: '/maps',
    },
    {
      text: t('APP_LAYOUT.MENU.ADS'),
      icon: 'ad',
      showIf:
        featureIsEnable.value('displaySplashAdvertisement') ||
        featureIsEnable.value('showDrawerBottomAdvertisement'),
      link: '/ads',
    },
    {
      text: t('APP_LAYOUT.MENU.STATIC_CFG'),
      icon: 'web',
      showIf: featureIsEnable.value('enableStaticWebsite'),
      link: '/staticCfgs',
    },
    {
      text: t('APP_LAYOUT.MENU.DASHBOARD_MATOMO'),
      icon: 'trending_up',
      link: '/dashboardAnalytics',
      showIf:
        featureIsEnable.value('enableMatomo') &&
        featureIsEnable.value('enableAnalyticsPage'),
    },
    {
      text: t('APP_LAYOUT.MENU.ADMIN'),
      icon: 'settings',
      showIf: isAdmin.value,
      subFields: [
        {
          text: t('APP_LAYOUT.MENU.USERS'),
          link: '/users',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.EXPORT'),
          link: '/publications',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.YURPLAN_TICKETS'),
          link: '/yurplan-tickets',
          showIf: featureIsEnable.value('yurplanTickets'),
        },
        {
          text: t('APP_LAYOUT.MENU.CONFIG_DEV'),
          link: '/applicationConfig',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.FILES'),
          link: '/orphaned',
          showIf: true,
        },
        {
          text: t('APP_LAYOUT.MENU.RESET'),
          link: '/reset',
          showIf: true,
        },
        {
          text: 'Api Docs',
          link: '/swagger',
          showIf: true,
        },
      ],
    },
  ]
})

function getFilteredFields<F extends { showIf: boolean }>(fields: F[]): F[] {
  return fields.filter((field) => field.showIf)
}

// Open the group holding the current page (e.g. on page load or deep link)
watch(
  () => route.path,
  () => {
    const activeGroup = getFilteredFields(menuFields.value).find(
      (field) => field.subFields && isGroupActive(field),
    )
    if (activeGroup && !isSubmenuOpen(activeGroup.text)) {
      openSubmenus.value = [...openSubmenus.value, activeGroup.text]
    }
  },
  { immediate: true },
)

async function logout() {
  await useSessionStore.getState().logout()
}

function publish() {
  usePublicationsStore.getState().showPublicationModalAction()
}
</script>
<style scoped>
.menu-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.4) transparent;
}
.menu-list::-webkit-scrollbar {
  width: 6px;
}
.menu-list::-webkit-scrollbar-track {
  background: transparent;
}
.menu-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 9999px;
}
.menu-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.6);
}
</style>
